import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Zap, ChevronLeft, Check } from "lucide-react";
import api, { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { WaysToUse } from "@/components/home/Sections";

const badgeList = ["100% Natural", "100% Veg", "No Preservatives", "No Added Colours", "No Artificial Flavours"];

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [showCharacter, setShowCharacter] = useState(false);

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => (await api.get(`/products/${slug}`)).data,
    retry: false,
  });

  const { data: all = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });

  useEffect(() => { setQty(1); setShowCharacter(false); }, [slug]);

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center pt-16"><div className="h-10 w-10 animate-spin rounded-full border-4 border-sand border-t-leaf" /></div>;
  }

  if (!product) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 pt-16 text-center">
        <p className="font-display text-4xl font-extrabold text-forest">This powder doesn&apos;t exist. Yet.</p>
        <Link to="/shop" data-testid="pdp-back-to-shop" className="rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream hover:bg-leaf">Back to the squad</Link>
      </div>
    );
  }

  const related = all.filter((p) => p.slug !== product.slug && p.tags.some((t) => product.tags.includes(t))).slice(0, 3);
  const fallbackRelated = all.filter((p) => p.slug !== product.slug).slice(0, 3);
  const relatedList = related.length >= 3 ? related : [...related, ...fallbackRelated].slice(0, 3);

  const buyNow = () => { add(product, qty, true); navigate("/checkout"); };

  return (
    <div className="pb-28 pt-24 lg:pb-0" data-testid="product-detail-page">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <nav className="flex items-center gap-1.5 py-6 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink/50" data-testid="pdp-breadcrumb">
          <Link to="/" className="hover:text-leaf">Home</Link> /
          <Link to="/shop" className="hover:text-leaf">Shop</Link> /
          <span className="text-forest">{product.name}</span>
        </nav>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ rotate: showCharacter ? 0 : -1.5, scale: 1.015 }}
              data-testid="pdp-image-panel"
              className="relative aspect-square overflow-hidden rounded-[2rem] border border-sand"
            >
              <div className="absolute inset-0" style={{ background: `radial-gradient(85% 75% at 50% 35%, ${product.accent}33 0%, transparent 72%)` }} />
              <img
                key={showCharacter ? "char" : "pack"}
                src={showCharacter ? product.character : product.image}
                alt={showCharacter ? `${product.mascot} mascot` : `${product.name} packaging`}
                className="absolute inset-0 h-full w-full object-cover p-8"
              />
              <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5 rounded-full bg-white/90 p-1.5 shadow-md backdrop-blur-sm">
                <button data-testid="pdp-view-packaging" onClick={() => setShowCharacter(false)} className={`rounded-full px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider ${!showCharacter ? "bg-forest text-cream" : "text-forest/60"}`}>Packaging</button>
                {product.character !== product.image && (
                  <button data-testid="pdp-view-mascot" onClick={() => setShowCharacter(true)} className={`rounded-full px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider ${showCharacter ? "bg-forest text-cream" : "text-forest/60"}`}>Mascot</button>
                )}
              </div>
            </motion.div>
          </div>

          <div className="lg:sticky lg:top-24 lg:self-start" data-testid="pdp-purchase-panel">
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: product.accent }}>
              {product.mascot} · {product.tagline}
            </p>
            <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-forest sm:text-5xl">{product.name}</h1>
            <p className="mt-3 text-base leading-relaxed text-ink/70 sm:text-lg">{product.benefit}</p>

            <div className="mt-5 flex items-center gap-3">
              <p className="font-display text-3xl font-extrabold text-forest">{inr(product.price)}</p>
              <span className="rounded-full bg-forest/5 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-forest">{product.pack}</span>
            </div>

            <ul className="mt-5 flex flex-wrap gap-2">
              {badgeList.map((b) => (
                <li key={b} className="flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-forest/70 ring-1 ring-sand">
                  <Check className="h-3 w-3 text-leaf" /> {b}
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <div className="flex items-center rounded-full border-2 border-sand bg-white" data-testid="pdp-qty-stepper">
                <button data-testid="pdp-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity" className="px-4 py-3 text-forest hover:text-leaf"><Minus className="h-4 w-4" /></button>
                <span data-testid="pdp-qty-value" className="w-10 text-center font-mono text-base font-bold">{qty}</span>
                <button data-testid="pdp-qty-plus" onClick={() => setQty(qty + 1)} aria-label="Increase quantity" className="px-4 py-3 text-forest hover:text-leaf"><Plus className="h-4 w-4" /></button>
              </div>
              <button
                data-testid="pdp-add-to-cart"
                onClick={() => add(product, qty)}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-leaf px-6 py-4 text-sm font-extrabold uppercase tracking-wide text-white transition-all hover:bg-forest active:scale-95"
              >
                <ShoppingBag className="h-4 w-4" /> Add to cart · {inr(product.price * qty)}
              </button>
              <button
                data-testid="pdp-buy-now"
                onClick={buyNow}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-forest px-6 py-4 text-sm font-extrabold uppercase tracking-wide text-cream transition-all hover:bg-leaf active:scale-95"
              >
                <Zap className="h-4 w-4" /> Buy now
              </button>
            </div>

            <div className="mt-8 divide-y divide-sand rounded-3xl border border-sand bg-white" data-testid="pdp-details">
              {[
                { t: "The story", b: product.description },
                { t: "Ingredients", b: product.ingredients },
                { t: "How to use", b: product.uses.map((u) => `${u.title} — ${u.desc}`).join(" ") },
                { t: "Storage", b: product.storage },
              ].map((row, i) => (
                <details key={row.t} open={i === 0} className="group px-6 py-4">
                  <summary data-testid={`pdp-detail-toggle-${i}`} className="flex cursor-pointer list-none items-center justify-between font-display text-sm font-extrabold uppercase tracking-wide text-forest">
                    {row.t}
                    <span className="text-leaf transition-transform group-open:rotate-45"><Plus className="h-4 w-4" /></span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-ink/70">{row.b}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>

      <WaysToUse product={product} />

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6" data-testid="pdp-related">
        <Reveal>
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-forest sm:text-4xl">COMPLETE THE SQUAD.</h2>
            <Link to="/shop" data-testid="pdp-related-view-all" className="group hidden items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-forest sm:flex hover:text-leaf">
              View all <ChevronLeft className="h-4 w-4 rotate-180 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
          {relatedList.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-sand bg-cream/95 p-3 px-4 backdrop-blur-md lg:hidden" data-testid="pdp-sticky-bar">
        <div className="flex-1">
          <p className="font-display text-sm font-extrabold text-forest">{product.name}</p>
          <p className="font-mono text-[11px] font-bold text-leaf">{inr(product.price)} · {product.pack}</p>
        </div>
        <button
          data-testid="pdp-sticky-add-to-cart"
          onClick={() => add(product, qty)}
          className="rounded-full bg-leaf px-6 py-3.5 text-xs font-extrabold uppercase tracking-wide text-white active:scale-95"
        >
          Add to cart
        </button>
      </div>
    </div>
  );
}
